import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Search, Loader2 } from 'lucide-react';
/**
 * GlobalSearch — the universal ⌘K omnibox shared by TekStak and GAS
 * (Phase 1 of docs/UNIVERSAL-SEARCH-ROADMAP.md).
 *
 * Presentational + interaction shell only: data fetching and navigation
 * are injected, because the two shells differ (TekStak: apiClient +
 * react-router; GAS: google.script.run proxy + view state).
 *
 * Interaction model (borrowed from the Quote Builder Omnibox):
 *   type      → debounced (150ms) suggest — title-prefix typeahead
 *   Enter     → full fused search, results grouped by entity type
 *   ↑/↓       → move highlight across the flattened list
 *   Enter on a highlighted row / click → onNavigate(hit)
 *   Esc       → close; ⌘K / Ctrl+K → focus from anywhere
 *
 * On Quote Builder pages the product omnibox registers its own ⌘K
 * later in the tree and wins focus there — intentional: in the quote
 * context ⌘K means "add products".
 */
const TYPE_LABEL = {
    department: 'Departments',
    vendor: 'Vendors',
    component: 'Products',
    contract: 'Contracts',
    quote: 'My Quotes',
    contact: 'Contacts',
    account: 'Accounts',
    opportunity: 'Opportunities',
    solicitation: 'Solicitations',
    po: 'Purchase Orders',
    sku: 'Catalog SKUs',
};
const typeLabel = (t) => TYPE_LABEL[t] ?? t;
/** Badge text for query-type suggestions (Phase 4 learning loop). */
const KIND_LABEL = {
    recent: 'Recent',
    popular: 'Popular',
    correction: 'Did you mean',
};
/** A query-type suggestion (recent/popular/correction) re-runs a search;
 * an entity suggestion navigates. Distinguished by kind + a `query` string. */
const isQuerySuggestion = (h) => !!h.query
    && h.kind !== undefined
    && h.kind !== 'entity';
/** Badge label for any row (entity type or query-suggestion kind). */
const rowLabel = (h) => isQuerySuggestion(h)
    ? KIND_LABEL[h.kind] ?? 'Search'
    : typeLabel(h.entityType);
/** Stable list key: query-suggestions have no entityId. */
const rowKey = (h, i) => isQuerySuggestion(h)
    ? `q-${h.kind}-${h.query}-${i}`
    : `${h.entityType}-${h.entityId}-${i}`;
export function GlobalSearch({ fetchSuggest, fetchSearch, onNavigate, logClick, placeholder = 'Search everything — quotes, vendors, contracts, contacts…', hotkey = true, debounceMs = 150, }) {
    const inputRef = React.useRef(null);
    const rootRef = React.useRef(null);
    const seqRef = React.useRef(0);
    // Set true right before we programmatically change the query to re-run a
    // full search (a picked correction/recent/popular) so the query-change
    // doesn't also fire the suggest effect and out-race the full results.
    const suppressSuggestRef = React.useRef(false);
    const [query, setQuery] = React.useState('');
    const [focused, setFocused] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [mode, setMode] = React.useState('suggest');
    const [rows, setRows] = React.useState([]);
    const [highlight, setHighlight] = React.useState(0);
    const [error, setError] = React.useState(null);
    const [didYouMean, setDidYouMean] = React.useState(null);
    const hitRows = rows.filter((r) => r.kind === 'hit');
    // ⌘K / Ctrl+K focuses from anywhere.
    React.useEffect(() => {
        if (!hotkey)
            return undefined;
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
                inputRef.current?.select();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [hotkey]);
    // Close on outside click.
    React.useEffect(() => {
        if (!focused)
            return undefined;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target))
                setFocused(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [focused]);
    // Suggest: empty focus → recents/popular (fetch on focus, no debounce);
    // typing (≥2) → debounced entity + popular completions.
    React.useEffect(() => {
        // Skip one run after a programmatic re-run (correction/recent/popular).
        if (suppressSuggestRef.current) {
            suppressSuggestRef.current = false;
            return undefined;
        }
        const q = query.trim();
        // Empty focus (Phase 4): show recent/popular. Only while focused, and
        // silent on error — an empty box should never surface an error banner.
        if (q.length < 2) {
            if (!focused) {
                setRows([]);
                setLoading(false);
                return undefined;
            }
            const seq = ++seqRef.current;
            let cancelled = false;
            (async () => {
                try {
                    const res = await fetchSuggest('');
                    if (cancelled || seq !== seqRef.current)
                        return;
                    setMode('suggest');
                    setError(null);
                    setRows(res.suggestions.map((hit, rank) => ({ kind: 'hit', hit, rank })));
                }
                catch {
                    if (!cancelled && seq === seqRef.current)
                        setRows([]);
                }
                finally {
                    if (!cancelled && seq === seqRef.current)
                        setLoading(false);
                }
            })();
            return () => { cancelled = true; };
        }
        const seq = ++seqRef.current;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await fetchSuggest(q);
                if (seq !== seqRef.current)
                    return; // stale response
                setMode('suggest');
                setError(null);
                setRows(res.suggestions.map((hit, rank) => ({ kind: 'hit', hit, rank })));
            }
            catch {
                if (seq === seqRef.current)
                    setError('Search is unavailable right now.');
            }
            finally {
                if (seq === seqRef.current)
                    setLoading(false);
            }
        }, debounceMs);
        return () => clearTimeout(t);
    }, [query, focused, debounceMs, fetchSuggest]);
    // -1 = nothing highlighted: Enter searches; arrows enter the list.
    React.useEffect(() => setHighlight(-1), [rows]);
    const runFullSearch = async (explicit) => {
        const q = (explicit ?? query).trim();
        if (q.length < 2)
            return;
        const seq = ++seqRef.current;
        setLoading(true);
        try {
            const res = await fetchSearch(q);
            if (seq !== seqRef.current)
                return;
            setMode('full');
            setError(null);
            setDidYouMean(res.didYouMean ?? null);
            const next = [];
            let rank = 0;
            for (const g of res.groups) {
                next.push({ kind: 'header', label: typeLabel(g.entityType) });
                for (const hit of g.hits)
                    next.push({ kind: 'hit', hit, rank: rank++ });
            }
            setRows(next);
        }
        catch {
            if (seq === seqRef.current)
                setError('Search is unavailable right now.');
        }
        finally {
            if (seq === seqRef.current)
                setLoading(false);
        }
    };
    // Re-run a full search under a new query string (correction / recent /
    // popular). Suppresses the suggest effect the query change would trigger.
    const runCorrection = (next) => {
        if (!next)
            return;
        suppressSuggestRef.current = true;
        setQuery(next);
        setHighlight(-1);
        void runFullSearch(next);
    };
    const go = (row) => {
        // A recent/popular/correction suggestion re-runs a search rather than
        // navigating to a record (Phase 4 learning loop).
        if (isQuerySuggestion(row.hit)) {
            runCorrection(String(row.hit.query || ''));
            return;
        }
        logClick?.(row.hit, row.rank, query.trim());
        onNavigate(row.hit, row.rank, query.trim());
        setFocused(false);
        setQuery('');
        setRows([]);
        inputRef.current?.blur();
    };
    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            setFocused(false);
            inputRef.current?.blur();
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, hitRows.length - 1));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, -1));
            return;
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            // Google model: Enter with nothing highlighted runs the full
            // grouped search; arrowing onto a row then Enter navigates to it.
            if (highlight >= 0 && hitRows[highlight])
                go(hitRows[highlight]);
            else
                void runFullSearch();
        }
    };
    const open = focused && (query.trim().length >= 2 || rows.length > 0 || !!error);
    return (_jsxs("div", { className: "gs-root", ref: rootRef, children: [_jsxs("div", { className: `gs-input${focused ? ' is-focused' : ''}`, children: [_jsx(Search, { size: 14, "aria-hidden": true }), _jsx("input", { ref: inputRef, type: "search", value: query, placeholder: placeholder, "aria-label": "Universal search", onFocus: () => setFocused(true), onChange: (e) => setQuery(e.target.value), onKeyDown: onKeyDown }), loading ? _jsx(Loader2, { size: 13, className: "gs-spin", "aria-hidden": true }) : _jsx("kbd", { children: "\u2318K" })] }), open && (_jsxs("div", { className: "gs-pop", role: "listbox", "aria-label": "Search results", children: [!error && mode === 'full' && didYouMean && (_jsxs("button", { type: "button", className: "gs-dym", onMouseDown: (e) => e.preventDefault(), onClick: () => runCorrection(didYouMean), children: ["Did you mean ", _jsx("strong", { children: didYouMean }), "?"] })), error && _jsx("div", { className: "gs-empty", children: error }), !error && rows.length === 0 && !loading && (_jsx("div", { className: "gs-empty", children: "No matches \u2014 press Enter for a deeper search." })), !error && rows.map((row, i) => row.kind === 'header' ? (_jsx("div", { className: "gs-sec", children: row.label }, `h-${row.label}-${i}`)) : (_jsxs("div", { role: "option", "aria-selected": highlight === row.rank, className: `gs-item${highlight === row.rank ? ' is-highlighted' : ''}`, onMouseEnter: () => setHighlight(row.rank), onMouseDown: (e) => e.preventDefault(), onClick: () => go(row), children: [_jsx("span", { className: `gs-badge gs-badge-${isQuerySuggestion(row.hit)
                                    ? row.hit.kind : row.hit.entityType}`, children: rowLabel(row.hit) }), _jsx("span", { className: "gs-title", children: row.hit.title }), row.hit.subtitle && _jsx("span", { className: "gs-sub", children: row.hit.subtitle })] }, rowKey(row.hit, i)))), !error && mode === 'suggest' && rows.length > 0 && (_jsx("div", { className: "gs-footer", children: "Enter \u2014 full search \u00B7 \u2191\u2193 \u2014 navigate \u00B7 Esc \u2014 close" }))] }))] }));
}
